import { ExitToApp } from "@mui/icons-material";
import { Switch, Typography } from "@mui/material";
import { ReactElement } from "react";

import {
  ListSubaccountsForStaffQuery,
  ListSubaccountsQuery,
} from "~frontend/__generated__/graphql";
import Table, { RowAction, RowActions } from "~frontend/components/Table";
import Archive from "~frontend/components/icons/Archive";
import Unarchive from "~frontend/components/icons/Unarchive";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";

type SubaccountRow =
  | ListSubaccountsQuery["listSubaccounts"][0]
  | ListSubaccountsForStaffQuery["listSubaccounts"][0];

export default function SubaccountTable({
  rows,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  totalCount,
  toggleArchiveSubaccount,
  setIsWeText,
}: {
  rows: SubaccountRow[];
  page: number;
  handleChangePage: (newPage: number) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  totalCount: number;
  toggleArchiveSubaccount: (
    subaccountId: string,
    archive: boolean
  ) => Promise<void>;
  setIsWeText?: (subaccountId: string, isWeText: boolean) => Promise<void>;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();

  function getRowActions(row: SubaccountRow): RowActions<SubaccountRow> {
    const archiveAction: RowAction<SubaccountRow> = {
      id: "archive",
      type: "button",
      icon: <Archive />,
      tooltipLabel: "Archive",
      confirm: {
        title: "Archive Subaccount",
        description: "Are you sure you want to archive this subaccount?",
      },
      async onClick() {
        await toggleArchiveSubaccount(row.id, true);
      },
    };

    const unarchiveAction: RowAction<SubaccountRow> = {
      id: "unarchive",
      type: "button",
      icon: <Unarchive />,
      tooltipLabel: "Unarchive",
      confirm: {
        title: "Unarchive Subaccount",
        description: "Are you sure you want to reinstate this subaccount?",
      },
      async onClick() {
        await toggleArchiveSubaccount(row.id, false);
      },
    };

    const goToSubaccountAction: RowAction<SubaccountRow> = {
      id: "go-to-subaccount",
      type: "link",
      icon: <ExitToApp />,
      tooltipLabel: "Go To Subaccount",
      href: (subaccount: SubaccountRow) =>
        `/subaccounts/${subaccount.slug}/campaigns`,
    };

    if (isStaff) {
      return row.archivedAt
        ? [unarchiveAction, goToSubaccountAction]
        : [archiveAction, goToSubaccountAction];
    }

    return [goToSubaccountAction];
  }

  return (
    <Table
      columns={[
        {
          key: "name",
          title: "Name",
          renderValue: subaccount => (
            <>
              <Typography data-testid="subaccount-name-cell">
                {subaccount.name}
                {subaccount.archivedAt ? " (ARCHIVED)" : ""}
              </Typography>
              <Typography variant="body3">{subaccount.id}</Typography>
            </>
          ),
        },
        {
          key: "createdAt",
          title: "Created At",
          renderValue: subaccount =>
            new Date(subaccount.createdAt).toLocaleString(),
        },
        ...(isStaff && setIsWeText
          ? [
              {
                key: "isWeText",
                title: "WeText?",
                renderValue: (subaccount: SubaccountRow) => {
                  const isWeTextRow =
                    subaccount as ListSubaccountsForStaffQuery["listSubaccounts"][0];
                  return (
                    <Switch
                      slotProps={{
                        input: {
                          "aria-label": `Toggle WeText for ${subaccount.name}`,
                        },
                      }}
                      checked={isWeTextRow.isWeText}
                      color="primary"
                      onChange={e =>
                        setIsWeText(subaccount.id, e.target.checked)
                      }
                    />
                  );
                },
              },
            ]
          : []),
      ]}
      rows={rows}
      rowActions={getRowActions}
      emptyState="No subaccounts found"
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      page={page}
      onChangePage={handleChangePage}
    />
  );
}
